/**
 * Consequence list. Screen spec 4.5 and S19.
 *
 * The enumeration that mixes what survives with what does not: a check in the
 * live colour for each survival, a cross in the down colour for each loss, and
 * both kinds in one list rather than two.
 *
 * MIXING THEM IS THE WHOLE POINT, and it is worth stating because the first
 * instinct in review is always to split them into "what you keep" and "what
 * you lose". A list of only losses reads as a scare screen and a person cannot
 * tell from it whether their code on GitHub is about to go with the project.
 * One list, in the order that answers the real question, is what makes the
 * screen read as honest rather than as a threat.
 *
 * It is a pattern rather than a private piece of the confirm dialog because
 * S19 prints the same enumeration on the page, before the button, and then the
 * dialog repeats it. Two copies of the most dangerous sentences in the product
 * is two chances for them to drift apart. The confirm dialog still draws its
 * own copy today; it should adopt this the next time it is opened.
 */

import '../../styles/components/consequence-list.css';

export interface ConsequenceItem {
  /** survives is a check in the live colour, loss is a cross in the down one. */
  kind: 'survives' | 'loss';
  text: string;
}

export interface ConsequenceListProps {
  items: ConsequenceItem[];
  /** Names the list for a screen reader: "What happens when you delete this". */
  label?: string;
}

export function ConsequenceList({ items, label }: ConsequenceListProps) {
  return (
    <ul className="conseq" aria-label={label}>
      {items.map((item) => (
        <li className="conseq__item" data-kind={item.kind} key={item.text}>
          <span className="conseq__glyph" aria-hidden="true">
            <svg viewBox="0 0 16 16" width="12" height="12" focusable="false">
              {item.kind === 'survives' ? (
                <path
                  d="M3 8.5 6.5 12 13 4.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ) : (
                <path
                  d="M4 4l8 8M12 4l-8 8"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              )}
            </svg>
          </span>
          <span className="conseq__text">{item.text}</span>
        </li>
      ))}
    </ul>
  );
}
